import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { attachRealtimeCall } from "../../api/realtime.api";
import { buildVoiceWsUrl } from "../../api/voiceApi";
import {
  decodeMuLawBase64ToFloat32,
  resampleLinear,
} from "../../utils/audio/mulaw";
import {
  decodePcm16Base64ToFloat32,
  encodeFloat32ToPcm16Base64,
} from "../../utils/audio/pcm16";

type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "closed"
  | "error";

export type AssistantMessage =
  | { type: "info"; text: string }
  | { type: "assistant_text"; text: string }
  | { type: "raw"; text: string };

type RealtimeState = {
  status: ConnectionStatus;
  messages: AssistantMessage[];
  audioEnabled: boolean;
  micStatus: "idle" | "enabled" | "error";
  micLevel: number;
};

type AgentType = "openai_audio" | "deepgram_tts" | "deepgram_agent";

type RealtimeActions = {
  connect: (options?: {
    seedContext?: unknown;
    agent?: string;
    agentType?: AgentType;
    medicalRecordRequestId?: string;
  }) => Promise<void>;
  teardown: () => void;
  sendHello: () => void;
  ensureAudioContext: () => Promise<void>;
  enableMicrophone: () => Promise<void>;
};

const REALTIME_URL = "https://api.openai.com/v1/realtime/calls";
const DEFAULT_SESSION_PATH = "/realtime/session";
const DEFAULT_ATTACH_PATH = "/realtime/attach";

const RealtimeStateContext = createContext<RealtimeState | null>(null);
const RealtimeActionsContext = createContext<RealtimeActions | null>(null);

export function useRealtimeState() {
  const ctx = useContext(RealtimeStateContext);
  if (!ctx) {
    throw new Error(
      "useRealtimeState must be used within RealtimeContextProvider"
    );
  }
  return ctx;
}

export function useRealtimeActions() {
  const ctx = useContext(RealtimeActionsContext);
  if (!ctx) {
    throw new Error(
      "useRealtimeActions must be used within RealtimeContextProvider"
    );
  }
  return ctx;
}

export default function RealtimeContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [micStatus, setMicStatus] = useState<"idle" | "enabled" | "error">(
    "idle"
  );
  const [micLevel, setMicLevel] = useState(0);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const micLevelRafRef = useRef<number | null>(null);
  const clientSecretRef = useRef<string | null>(null);
  const ttsWsRef = useRef<WebSocket | null>(null);
  const ttsNextTimeRef = useRef<number | null>(null);
  const deepgramWsRef = useRef<WebSocket | null>(null);
  const deepgramInputContextRef = useRef<AudioContext | null>(null);
  const deepgramProcessorRef = useRef<ScriptProcessorNode | null>(null);

  const sessionEndpoint = useMemo(() => {
    const resolveVoiceBaseUrl = () => {
      const configured =
        process.env.REACT_APP_VOICE_API_URL ??
        process.env.NEXT_PUBLIC_VOICE_API_URL ??
        process.env.VITE_VOICE_API_URL ??
        process.env.REACT_APP_VOICE_API;

      if (configured) {
        return configured.replace(/\/+$/, "");
      }

      if (typeof window !== "undefined") {
        if (window.location.hostname === "localhost") {
          return "http://localhost:3003";
        }
      }

      return "https://voice.harper.new";
    };

    const normalizeSessionUrl = (rawUrl: string) => {
      const trimmed = rawUrl.trim();
      if (!trimmed || trimmed === "/") {
        return DEFAULT_SESSION_PATH;
      }
      if (trimmed.startsWith("/")) {
        return trimmed;
      }
      try {
        const url = new URL(trimmed);
        if (!url.pathname || url.pathname === "/") {
          url.pathname = DEFAULT_SESSION_PATH;
        }
        return url.toString();
      } catch {
        return trimmed;
      }
    };

    const envUrl =
      process.env.REACT_APP_REALTIME_SESSION_URL ??
      process.env.NEXT_PUBLIC_REALTIME_SESSION_URL ??
      process.env.VITE_REALTIME_SESSION_URL ??
      process.env.REACT_APP_REALTIME_TOKEN_URL ??
      process.env.NEXT_PUBLIC_REALTIME_TOKEN_URL ??
      process.env.VITE_REALTIME_TOKEN_URL ??
      process.env.REACT_APP_VOICE_API;
    if (envUrl) return normalizeSessionUrl(envUrl);

    return `${resolveVoiceBaseUrl()}${DEFAULT_SESSION_PATH}`;
  }, []);

  const attachEndpoint = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_REALTIME_ATTACH_URL ??
      process.env.NEXT_PUBLIC_REALTIME_ATTACH_URL ??
      process.env.VITE_REALTIME_ATTACH_URL;
    if (envUrl) return envUrl;

    const deriveAttachPath = (sessionUrl: string) => {
      if (sessionUrl.endsWith("/realtime/session")) {
        return sessionUrl.replace(/\/realtime\/session$/, "/realtime/attach");
      }
      return DEFAULT_ATTACH_PATH;
    };

    try {
      const url = new URL(
        sessionEndpoint,
        typeof window !== "undefined" ? window.location.origin : undefined
      );
      const derived = deriveAttachPath(url.pathname);
      return new URL(derived, url.origin).toString();
    } catch {
      return deriveAttachPath(sessionEndpoint);
    }
  }, [sessionEndpoint]);

  const appendMessage = useCallback((msg: AssistantMessage) => {
    setMessages((prev) => [...prev, msg].slice(-100));
  }, []);

  const ensureAudioContext = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) {
          appendMessage({
            type: "info",
            text: "AudioContext not supported in this browser.",
          });
          return;
        }
        audioContextRef.current = new Ctor();
      }
      if (audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume();
      }
      const ctx = audioContextRef.current;
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      setAudioEnabled(true);
    } catch (err) {
      console.error("Unable to unlock audio", err);
      appendMessage({ type: "info", text: "Failed to enable audio playback." });
    }
  }, [appendMessage]);

  const startMicMeter = useCallback(() => {
    try {
      if (!micStreamRef.current) return;
      if (!audioContextRef.current) {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return;
        audioContextRef.current = new Ctor();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") {
        ctx.resume();
      }

      const source = ctx.createMediaStreamSource(micStreamRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      micAnalyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = dataArray[i] - 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const level = Math.min(100, Math.max(0, Math.round((rms / 128) * 100)));
        setMicLevel(level);
        micLevelRafRef.current = requestAnimationFrame(tick);
      };
      micLevelRafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error("Unable to start mic meter", err);
    }
  }, []);

  const enableMicrophone = useCallback(async () => {
    try {
      if (micStreamRef.current) {
        setMicStatus("enabled");
        startMicMeter();
        appendMessage({ type: "info", text: "Microphone already enabled." });
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      setMicStatus("enabled");
      startMicMeter();
      appendMessage({ type: "info", text: "Microphone access granted." });
    } catch (err) {
      console.error("Microphone access error", err);
      setMicStatus("error");
      appendMessage({
        type: "info",
        text: "Microphone access denied or unavailable.",
      });
    }
  }, [appendMessage, startMicMeter]);

  const setupRemoteAudio = useCallback(
    async (event: RTCTrackEvent) => {
      try {
        await ensureAudioContext();
        if (!audioElementRef.current) {
          audioElementRef.current = document.createElement("audio");
          audioElementRef.current.autoplay = true;
        }
        const stream = event.streams[0];
        audioElementRef.current.srcObject = stream;
        await audioElementRef.current.play();
        appendMessage({ type: "info", text: "Remote audio stream playing." });
      } catch (err) {
        console.error("Unable to play remote audio", err);
        appendMessage({
          type: "info",
          text: "Remote audio blocked; click Enable audio playback and retry.",
        });
      }
    },
    [appendMessage, ensureAudioContext]
  );

  const fetchEphemeralKey = useCallback(
    async (options?: { agent?: string; agentType?: AgentType }): Promise<string> => {
      const payload =
        options?.agent || options?.agentType
          ? {
              ...(options?.agent ? { agent: options.agent } : {}),
              ...(options?.agentType ? { agent_type: options.agentType } : {}),
            }
          : null;
      const res = await fetch(sessionEndpoint, {
        method: payload ? "POST" : "GET",
        headers: payload ? { "Content-Type": "application/json" } : undefined,
        body: payload ? JSON.stringify(payload) : undefined,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch token (${res.status})`);
      }
      const data = await res.json();
      const key = data?.client_secret ?? data?.value ?? data?.token ?? data?.key;
      if (!key) throw new Error("Token response missing value");
      clientSecretRef.current = key;
      return key;
    },
    [sessionEndpoint]
  );

  const teardown = useCallback(() => {
    if (deepgramWsRef.current) {
      try {
        deepgramWsRef.current.close();
      } catch (err) {
        console.error("Error closing Deepgram WebSocket", err);
      }
      deepgramWsRef.current = null;
    }
    if (deepgramProcessorRef.current) {
      try {
        deepgramProcessorRef.current.disconnect();
      } catch (err) {
        console.error("Error disconnecting Deepgram processor", err);
      }
      deepgramProcessorRef.current = null;
    }
    if (deepgramInputContextRef.current) {
      deepgramInputContextRef
        .current!.close()
        .catch((err) =>
          console.error("Error closing Deepgram audio context", err)
        );
      deepgramInputContextRef.current = null;
    }
    if (ttsWsRef.current) {
      try {
        ttsWsRef.current.close();
      } catch (err) {
        console.error("Error closing TTS WebSocket", err);
      }
      ttsWsRef.current = null;
    }
    ttsNextTimeRef.current = null;
    try {
      dataChannelRef.current?.close();
      dataChannelRef.current = null;
    } catch (err) {
      console.error("Error closing data channel", err);
    }
    try {
      pcRef.current?.getSenders().forEach((s) => s.track?.stop());
      pcRef.current?.close();
    } catch (err) {
      console.error("Error closing peer connection", err);
    }
    pcRef.current = null;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (micLevelRafRef.current !== null) {
      cancelAnimationFrame(micLevelRafRef.current);
      micLevelRafRef.current = null;
    }
    if (micAnalyserRef.current) {
      micAnalyserRef.current.disconnect();
      micAnalyserRef.current = null;
    }
    setStatus("closed");
  }, []);

  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  const connect = useCallback(async (options?: {
    seedContext?: unknown;
    agent?: string;
    agentType?: AgentType;
    medicalRecordRequestId?: string;
  }) => {
    const seedContext = options?.seedContext;
    const agent = options?.agent;
    const agentType = options?.agentType;
    const medicalRecordRequestId = options?.medicalRecordRequestId;
    if (status === "connected" || status === "connecting") return;
    setStatus("connecting");
    appendMessage({ type: "info", text: "Starting realtime session..." });

    try {
      await ensureAudioContext();
      await enableMicrophone();

      if (agentType === "deepgram_agent") {
        if (deepgramWsRef.current) {
          deepgramWsRef.current.close();
        }
        ttsNextTimeRef.current = null;
        const ws = new WebSocket(buildVoiceWsUrl("/connection/deepgram"));
        deepgramWsRef.current = ws;

        ws.onopen = async () => {
          const startPayload = {
            type: "start",
            ...(agent ? { agent } : {}),
            ...(seedContext ? { seed_context: seedContext } : {}),
            ...(medicalRecordRequestId
              ? { medical_record_request_id: medicalRecordRequestId }
              : {}),
          };
          ws.send(JSON.stringify(startPayload));

          if (micStreamRef.current) {
            const inputCtx = new AudioContext({ sampleRate: 16000 });
            deepgramInputContextRef.current = inputCtx;
            if (inputCtx.state === "suspended") {
              await inputCtx.resume();
            }
            const source = inputCtx.createMediaStreamSource(
              micStreamRef.current
            );
            const processor = inputCtx.createScriptProcessor(4096, 1, 1);
            deepgramProcessorRef.current = processor;
            const silentGain = inputCtx.createGain();
            silentGain.gain.value = 0;
            processor.onaudioprocess = (event) => {
              if (ws.readyState !== WebSocket.OPEN) return;
              const input = event.inputBuffer.getChannelData(0);
              const encoded = encodeFloat32ToPcm16Base64(input);
              ws.send(
                JSON.stringify({
                  type: "audio",
                  audio: encoded,
                })
              );
            };
            source.connect(processor);
            processor.connect(silentGain);
            silentGain.connect(inputCtx.destination);
          }
        };

        ws.onmessage = (event) => {
          if (typeof event.data !== "string") return;
          try {
            const payload = JSON.parse(event.data);
            if (payload?.type === "call_id") {
              appendMessage({
                type: "info",
                text: `Deepgram call id: ${payload.call_id}`,
              });
              setStatus("connected");
              return;
            }
            if (payload?.type !== "audio" || !payload.audio) return;

            const decoded = decodePcm16Base64ToFloat32(payload.audio);
            const audioCtx = audioContextRef.current;
            if (!audioCtx) return;
            const resampled = resampleLinear(
              decoded,
              16000,
              audioCtx.sampleRate
            );
            const buffer = audioCtx.createBuffer(
              1,
              resampled.length,
              audioCtx.sampleRate
            );
            buffer.getChannelData(0).set(resampled);
            const source = audioCtx.createBufferSource();
            source.buffer = buffer;
            source.connect(audioCtx.destination);
            const now = audioCtx.currentTime + 0.05;
            const startAt =
              ttsNextTimeRef.current && ttsNextTimeRef.current > now
                ? ttsNextTimeRef.current
                : now;
            source.start(startAt);
            ttsNextTimeRef.current = startAt + buffer.duration;
          } catch (err) {
            console.error("Deepgram WS message error", err);
          }
        };

        ws.onerror = (err) => {
          console.error("Deepgram WS error", err);
          setStatus("error");
        };

        ws.onclose = () => {
          setStatus("closed");
        };

        return;
      }

      const token = await fetchEphemeralKey({ agent, agentType });
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.ontrack = (e) => {
        setupRemoteAudio(e);
      };
      pc.onconnectionstatechange = () => {
        if (!pcRef.current) return;
        const state = pcRef.current.connectionState;
        if (state === "connected") setStatus("connected");
        if (
          state === "disconnected" ||
          state === "failed" ||
          state === "closed"
        ) {
          setStatus("closed");
        }
      };

      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;

      dc.onopen = () => {
        appendMessage({ type: "info", text: "Data channel open." });
      };
      dc.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.event === "response.text.delta") {
            appendMessage({ type: "assistant_text", text: parsed?.text ?? "" });
          } else {
            appendMessage({ type: "raw", text: event.data });
          }
        } catch {
          appendMessage({ type: "raw", text: String(event.data) });
        }
      };
      dc.onerror = (event) => {
        console.error("DataChannel error", event);
        appendMessage({ type: "info", text: "Data channel error." });
      };

      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach((track) => {
          pc.addTrack(track, micStreamRef.current as MediaStream);
        });
      }

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(REALTIME_URL, {
        method: "POST",
        body: offer.sdp ?? "",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/sdp",
        },
      });

      if (!sdpResponse.ok) {
        throw new Error(`Realtime API error: ${sdpResponse.status}`);
      }

      const locationHeader = sdpResponse.headers.get("Location");
      const callId = locationHeader?.split("/").pop();
      if (!callId) {
        throw new Error("Realtime API missing call id.");
      }
      console.log("Realtime callId from Location header:", callId);

      if (agentType === "deepgram_tts") {
        if (ttsWsRef.current) {
          ttsWsRef.current.close();
        }
        ttsNextTimeRef.current = null;
        const wsUrl = buildVoiceWsUrl(
          `/connection/tts?callId=${encodeURIComponent(callId)}`
        );
        const ws = new WebSocket(wsUrl);
        ttsWsRef.current = ws;

        ws.onmessage = (event) => {
          if (typeof event.data !== "string") return;
          try {
            const payload = JSON.parse(event.data);
            if (payload?.type !== "tts_audio") return;
            if (!payload.audio) return;

            const decoded = decodeMuLawBase64ToFloat32(payload.audio);
            const audioCtx = audioContextRef.current;
            if (!audioCtx) return;
            const resampled = resampleLinear(decoded, 8000, audioCtx.sampleRate);
            const buffer = audioCtx.createBuffer(
              1,
              resampled.length,
              audioCtx.sampleRate
            );
            buffer.getChannelData(0).set(resampled);

            const source = audioCtx.createBufferSource();
            source.buffer = buffer;
            source.connect(audioCtx.destination);
            const now = audioCtx.currentTime + 0.05;
            const startAt =
              ttsNextTimeRef.current && ttsNextTimeRef.current > now
                ? ttsNextTimeRef.current
                : now;
            source.start(startAt);
            ttsNextTimeRef.current = startAt + buffer.duration;
          } catch (err) {
            console.error("TTS WS message error", err);
          }
        };
        ws.onerror = (err) => {
          console.error("TTS WS error", err);
        };
      }

      const attachResponse = await attachRealtimeCall({
        attachEndpoint,
        callId,
        clientSecret: clientSecretRef.current ?? token,
        seedContext,
        agent,
        agentType,
      });
      if (!attachResponse.ok) {
        appendMessage({
          type: "info",
          text: `Attach failed (${attachResponse.status}).`,
        });
        throw new Error(
          `Failed to attach realtime call (${attachResponse.status})`
        );
      }
      appendMessage({ type: "info", text: "Realtime call attached." });

      const answer = {
        type: "answer" as const,
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);

      appendMessage({ type: "info", text: "Realtime session established." });
      setStatus("connected");
    } catch (err) {
      console.error("Realtime setup failed", err);
      appendMessage({
        type: "info",
        text: err instanceof Error ? err.message : "Failed to start session.",
      });
      setStatus("error");
      teardown();
    }
  }, [
    appendMessage,
    enableMicrophone,
    ensureAudioContext,
    fetchEphemeralKey,
    setupRemoteAudio,
    status,
    teardown,
    attachEndpoint,
  ]);

  const sendHello = useCallback(() => {
    if (
      !dataChannelRef.current ||
      dataChannelRef.current.readyState !== "open"
    ) {
      appendMessage({ type: "info", text: "Data channel not open yet." });
      return;
    }
    const payload = {
      type: "input_text",
      text: "Hello Harper",
    };
    dataChannelRef.current.send(JSON.stringify(payload));
    appendMessage({ type: "info", text: "Sent: Hello Harper" });
  }, [appendMessage]);

  const stateValue = useMemo(
    () => ({
      status,
      messages,
      audioEnabled,
      micStatus,
      micLevel,
    }),
    [audioEnabled, messages, micLevel, micStatus, status]
  );

  const actionsValue = useMemo(
    () => ({
      connect,
      teardown,
      sendHello,
      ensureAudioContext,
      enableMicrophone,
    }),
    [connect, enableMicrophone, ensureAudioContext, sendHello, teardown]
  );

  return (
    <RealtimeStateContext.Provider value={stateValue}>
      <RealtimeActionsContext.Provider value={actionsValue}>
        {children}
      </RealtimeActionsContext.Provider>
    </RealtimeStateContext.Provider>
  );
}
